'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { ChevronLeft, ChevronRight, Menu, X, CheckCircle, Lock, PlayCircle, FileText, HelpCircle, MessageSquare, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';

const lessonList = [
{ id: 'lp-001', title: 'Welcome & Course Overview', type: 'video', duration: '5:42', completed: true, locked: false },
{ id: 'lp-002', title: 'What is Machine Learning?', type: 'video', duration: '18:20', completed: true, locked: false },
{ id: 'lp-003', title: 'Setting Up Your Python Environment', type: 'reading', duration: '12 min', completed: true, locked: false },
{ id: 'lp-004', title: 'Supervised vs Unsupervised Learning', type: 'video', duration: '22:10', completed: false, locked: false, current: true },
{ id: 'lp-005', title: 'Your First ML Model: Linear Regression', type: 'video', duration: '34:55', completed: false, locked: false },
{ id: 'lp-006', title: 'Quiz: Core ML Concepts', type: 'quiz', duration: '10 min', completed: false, locked: true },
{ id: 'lp-007', title: 'Classification with Logistic Regression', type: 'video', duration: '28:40', completed: false, locked: true },
{ id: 'lp-008', title: 'Decision Trees & Random Forests', type: 'video', duration: '41:12', completed: false, locked: true },
{ id: 'lp-009', title: 'Feature Engineering Deep Dive', type: 'video', duration: '35:28', completed: false, locked: true },
{ id: 'lp-010', title: 'Model Evaluation & Cross-Validation', type: 'video', duration: '29:05', completed: false, locked: true },
{ id: 'lp-011', title: 'Hyperparameter Tuning', type: 'video', duration: '24:33', completed: false, locked: true },
{ id: 'lp-012', title: 'Quiz: Model Selection', type: 'quiz', duration: '15 min', completed: false, locked: true }];


const lessonTypeIcon = (type: string, size = 13) => {
  if (type === 'video') return <PlayCircle size={size} className="text-primary" />;
  if (type === 'reading') return <FileText size={size} className="text-blue-500" />;
  if (type === 'quiz') return <HelpCircle size={size} className="text-amber-500" />;
  return <PlayCircle size={size} />;
};

const tabs = ['Overview', 'Notes', 'Resources', 'Discussion'] as const;
type Tab = typeof tabs[number];

export default function LessonPlayerLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('Overview');
  const [noteText, setNoteText] = useState('');
  const [notes, setNotes] = useState<{id: string;text: string;time: string;}[]>([
  { id: 'note-1', text: 'Supervised learning requires labelled data — key distinction from unsupervised.', time: '03:22' },
  { id: 'note-2', text: 'Remember: bias-variance tradeoff is central to all model selection decisions.', time: '11:45' }]
  );
  const [markingComplete, setMarkingComplete] = useState(false);

  const currentLesson = lessonList.find((l) => l.current);
  const currentIndex = lessonList.findIndex((l) => l.current);
  const prevLesson = currentIndex > 0 ? lessonList[currentIndex - 1] : null;
  const nextLesson = currentIndex < lessonList.length - 1 ? lessonList[currentIndex + 1] : null;
  const completedCount = lessonList.filter((l) => l.completed).length;
  const progressPct = Math.round(completedCount / lessonList.length * 100);

  const handleMarkComplete = async () => {
    setMarkingComplete(true);
    // Backend integration point: PATCH /api/enrollments/:id/lessons/:lessonId with { completed: true }
    await new Promise((r) => setTimeout(r, 800));
    setMarkingComplete(false);
    toast.success('Lesson marked complete!', { description: 'Great work — 4/12 lessons done.' });
  };

  const addNote = () => {
    if (!noteText.trim()) return;
    setNotes((prev) => [{ id: `note-${prev.length + 1}`, text: noteText.trim(), time: '22:10' }, ...prev]);
    setNoteText('');
    toast.success('Note saved');
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex flex-col">
      {/* Top bar */}
      <header className="h-14 bg-[#1E293B] border-b border-white/10 flex items-center px-4 gap-4 flex-shrink-0 z-20">
        <Link href="/course-catalogue" className="flex items-center gap-2 flex-shrink-0">
          <AppLogo size={28} />
          <span className="font-bold text-sm text-white hidden sm:block">EugeneTechRN</span>
        </Link>
        <div className="w-px h-5 bg-white/10 hidden sm:block" />
        <div className="flex-1 min-w-0 hidden sm:block">
          <p className="text-xs text-white/50 truncate">Machine Learning Fundamentals</p>
          <p className="text-sm font-semibold text-white truncate">{currentLesson?.title}</p>
        </div>

        {/* Progress */}
        <div className="hidden md:flex items-center gap-3">
          <div className="w-32 h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full progress-bar-fill" style={{ width: `${progressPct}%` }} />
          </div>
          <span className="text-xs text-white/50 font-tabular">{completedCount}/{lessonList.length}</span>
        </div>

        <button
          onClick={handleMarkComplete}
          disabled={markingComplete}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-success text-white hover:bg-success/90 transition-colors disabled:opacity-60 flex-shrink-0">
          
          {markingComplete ?
          <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.3" />
              <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            </svg> :

          <CheckCircle size={13} />
          }
          Mark Complete
        </button>

        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors flex-shrink-0"
          aria-label="Toggle lesson sidebar">
          
          {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Main player area */}
        <div className="flex-1 flex flex-col min-w-0 overflow-y-auto">
          {/* Video player */}
          <div className="relative bg-black" style={{ paddingBottom: '56.25%' }}>
            <div className="absolute inset-0 flex items-center justify-center">
              <img
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1044e4cf4-1772767373429.png"
                alt="Video lesson: Supervised vs Unsupervised Learning — neural network diagram on dark background"
                className="w-full h-full object-cover opacity-60" />
              
              {/* Play button overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <button className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors shadow-modal">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="var(--primary)">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
              </div>
              {/* Fake progress bar */}
              <div className="absolute bottom-0 left-0 right-0 px-4 pb-3">
                <div className="flex items-center gap-3 text-white text-xs mb-2">
                  <span className="font-tabular">11:45</span>
                  <div className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden cursor-pointer">
                    <div className="h-full bg-primary rounded-full" style={{ width: '53%' }} />
                  </div>
                  <span className="font-tabular">22:10</span>
                </div>
              </div>
            </div>
          </div>

          {/* Lesson content area */}
          <div className="bg-background flex-1">
            {/* Prev / Next nav */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <button
                className={`flex items-center gap-2 text-sm font-medium transition-colors ${prevLesson ? 'text-muted-foreground hover:text-foreground' : 'text-muted opacity-40 cursor-not-allowed'}`}
                disabled={!prevLesson}>
                
                <ChevronLeft size={16} />
                {prevLesson ? prevLesson.title : 'No previous lesson'}
              </button>
              <button
                className={`flex items-center gap-2 text-sm font-medium transition-colors ${nextLesson ? 'text-primary hover:text-primary/80' : 'text-muted opacity-40 cursor-not-allowed'}`}
                disabled={!nextLesson}>
                
                {nextLesson ? nextLesson.title : 'Course complete'}
                <ChevronRight size={16} />
              </button>
            </div>

            {/* Tabs */}
            <div className="border-b border-border px-5">
              <div className="flex gap-1">
                {tabs.map((t) =>
                <button
                  key={`lp-tab-${t}`}
                  onClick={() => setActiveTab(t)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-all duration-150 ${
                  activeTab === t ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`
                  }>
                  
                    {t}
                  </button>
                )}
              </div>
            </div>

            <div className="p-5 max-w-3xl">
              {activeTab === 'Overview' &&
              <div className="fade-in space-y-4">
                  <h2 className="text-xl font-bold text-foreground">Supervised vs Unsupervised Learning</h2>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    In this lesson, we draw the fundamental distinction between supervised and unsupervised learning paradigms. You&apos;ll understand when to apply each approach, the role of labelled data, and how common algorithms fit within each category.
                  </p>
                  <div>
                    <h3 className="font-semibold text-foreground text-sm mb-2">Lesson objectives</h3>
                    <ul className="space-y-2">
                      {[
                    'Define supervised and unsupervised learning with concrete examples',
                    'Identify the role of labelled vs unlabelled datasets',
                    'Map common algorithms (SVM, k-means, PCA) to their paradigm',
                    'Understand semi-supervised and self-supervised learning as extensions'].
                    map((obj, i) =>
                    <li key={`obj-${i}`} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle size={14} className="text-success mt-0.5 flex-shrink-0" />
                          {obj}
                        </li>
                    )}
                    </ul>
                  </div>
                  <div className="bg-muted rounded-xl p-4">
                    <p className="text-xs font-semibold text-foreground mb-2 uppercase tracking-wide">Code snippet from this lesson</p>
                    <pre className="font-mono text-xs text-foreground overflow-x-auto">
{`from sklearn.linear_model import LogisticRegression
from sklearn.cluster import KMeans

# Supervised: requires labels (y_train)
clf = LogisticRegression()
clf.fit(X_train, y_train)

# Unsupervised: no labels needed
kmeans = KMeans(n_clusters=3)
kmeans.fit(X_train)`}
                    </pre>
                  </div>
                </div>
              }

              {activeTab === 'Notes' &&
              <div className="fade-in space-y-4">
                  <div className="flex gap-2">
                    <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add a note at the current timestamp..."
                    className="input-field flex-1 min-h-[80px] resize-none" />
                  
                  </div>
                  <button onClick={addNote} className="btn-primary py-2 text-sm">
                    <StickyNote size={14} />
                    Save Note
                  </button>
                  <div className="space-y-3 mt-4">
                    {notes.map((n) =>
                  <div key={n.id} className="card p-3 flex gap-3">
                        <span className="text-xs font-mono font-semibold text-primary flex-shrink-0 mt-0.5">{n.time}</span>
                        <p className="text-sm text-foreground leading-snug flex-1">{n.text}</p>
                      </div>
                  )}
                  </div>
                </div>
              }

              {activeTab === 'Resources' &&
              <div className="fade-in space-y-3">
                  <p className="text-sm text-muted-foreground mb-3">Supplementary materials for this lesson</p>
                  {[
                { id: 'res-1', type: 'PDF', title: 'Supervised vs Unsupervised Learning — Cheat Sheet', size: '245 KB' },
                { id: 'res-2', type: 'Link', title: 'scikit-learn Documentation: Supervised Learning', url: '#' },
                { id: 'res-3', type: 'Notebook', title: 'Lesson 4 Starter Notebook (.ipynb)', size: '18 KB' }].
                map((r) =>
                <div key={r.id} className="card p-3 flex items-center gap-3 hover:bg-muted/50 cursor-pointer transition-colors">
                      <span className="text-xs font-bold bg-primary/10 text-primary px-2 py-0.5 rounded-md">{r.type}</span>
                      <span className="flex-1 text-sm text-foreground">{r.title}</span>
                      {r.size && <span className="text-xs text-muted-foreground">{r.size}</span>}
                    </div>
                )}
                </div>
              }

              {activeTab === 'Discussion' &&
              <div className="fade-in space-y-4">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      SA
                    </div>
                    <div className="flex-1">
                      <textarea
                      placeholder="Ask a question about this lesson..."
                      className="input-field w-full min-h-[72px] resize-none" />
                    
                      <button className="btn-primary mt-2 py-2 text-sm">
                        <MessageSquare size={14} />
                        Post Question
                      </button>
                    </div>
                  </div>
                  {[
                {
                  id: 'disc-1',
                  author: 'Ngozi Eze',
                  avatar: 'NE',
                  bg: 'bg-emerald-500',
                  time: '2 days ago',
                  text: 'Is there a practical difference between semi-supervised and self-supervised in terms of data labelling effort?',
                  replies: 3
                },
                {
                  id: 'disc-2',
                  author: 'Rafael Mendes',
                  avatar: 'RM',
                  bg: 'bg-blue-500',
                  time: '5 days ago',
                  text: 'The k-means example around 14:30 was really clarifying. Does cluster count always need to be defined upfront?',
                  replies: 7
                }].
                map((d) =>
                <div key={d.id} className="flex gap-3 border-t border-border pt-4">
                      <div className={`w-8 h-8 rounded-full ${d.bg} text-white flex items-center justify-center text-xs font-bold flex-shrink-0`}>
                        {d.avatar}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-foreground">{d.author}</span>
                          <span className="text-xs text-muted-foreground">{d.time}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{d.text}</p>
                        <button className="text-xs text-primary font-medium mt-1 hover:underline flex items-center gap-1">
                          <MessageSquare size={11} /> {d.replies} replies
                        </button>
                      </div>
                    </div>
                )}
                </div>
              }
            </div>
          </div>
        </div>

        {/* Lesson sidebar */}
        {sidebarOpen &&
        <aside className="w-72 xl:w-80 bg-[#1E293B] border-l border-white/10 flex flex-col flex-shrink-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <div>
                <p className="text-xs text-white/50 font-medium">Course content</p>
                <p className="text-sm font-semibold text-white">ML Fundamentals</p>
              </div>
              <span className="text-xs text-white/40 font-tabular">{completedCount}/{lessonList.length}</span>
            </div>
            <div className="flex-1 overflow-y-auto scrollbar-thin">
              {lessonList.map((lesson, idx) => {
              const isCurrent = lesson.current;
              return (
                <div
                  key={lesson.id}
                  className={`flex items-center gap-3 px-4 py-3 border-b border-white/5 cursor-pointer transition-colors
                      ${isCurrent ? 'bg-primary/20' : 'hover:bg-white/5'}
                      ${lesson.locked ? 'opacity-50 cursor-not-allowed' : ''}
                    `}>
                  
                    <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                      {lesson.completed ?
                    <CheckCircle size={14} className="text-success" /> :
                    lesson.locked ?
                    <Lock size={12} className="text-white/30" /> :

                    <span className="text-xs font-tabular text-white/40">{idx + 1}</span>
                    }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs leading-snug truncate ${isCurrent ? 'text-white font-semibold' : lesson.completed ? 'text-white/40 line-through' : 'text-white/70'}`}>
                        {lesson.title}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {lessonTypeIcon(lesson.type, 10)}
                        <span className="text-xs text-white/30 font-tabular">{lesson.duration}</span>
                      </div>
                    </div>
                    {isCurrent &&
                  <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                  }
                  </div>);

            })}
            </div>
            {/* Sidebar footer */}
            <div className="p-4 border-t border-white/10">
              <div className="flex items-center justify-between text-xs text-white/40 mb-2">
                <span>Overall progress</span>
                <span className="font-tabular">{progressPct}%</span>
              </div>
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full progress-bar-fill" style={{ width: `${progressPct}%` }} />
              </div>
            </div>
          </aside>
        }
      </div>
    </div>);

}